import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { meetingsApi, meetingTemplatesApi, usersApi, memberRecordsApi, goalsApi, milestonesApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Calendar, Users, FileText, Clock, Trash2, ChevronRight, Target, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatCurrency } from '../lib/utils';

export default function Meetings() {
  const { isAdmin, isAdminOrFounder, canDelete } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [meetingsRes, templatesRes, usersRes, goalsRes] = await Promise.all([
        meetingsApi.getAll(),
        meetingTemplatesApi.getAll(),
        usersApi.getMembers(),
        goalsApi.getAll()
      ]);
      setMeetings(meetingsRes.data);
      setTemplates(templatesRes.data);
      setUsers(usersRes.data);
      setGoals(goalsRes.data);
    } catch (error) {
      toast.error('Failed to load meetings');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this meeting?')) return;
    try {
      await meetingsApi.delete(id);
      setMeetings(meetings.filter(m => m.id !== id));
      toast.success('Meeting deleted');
    } catch (error) {
      toast.error('Failed to delete meeting');
    }
  };

  const getUserName = (userId) => users.find(u => u.id === userId)?.name || 'Unknown';

  if (loading) {
    return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-32 skeleton rounded-lg" />)}</div>;
  }

  return (
    <div className="space-y-6" data-testid="meetings-page">
      <div className="flex items-center justify-between">
        <div className="page-header mb-0">
          <h1 className="page-title">Meetings</h1>
          <p className="page-subtitle">Schedule and manage team meetings</p>
        </div>
        {isAdminOrFounder() && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-meeting-btn">
                <Plus className="w-4 h-4 mr-2" /> Create Meeting
              </Button>
            </DialogTrigger>
            <CreateMeetingDialog
              templates={templates}
              users={users}
              goals={goals}
              onClose={() => setDialogOpen(false)}
              onSave={loadData}
            />
          </Dialog>
        )}
      </div>

      {/* Meetings List or Detail */}
      {selectedMeeting ? (
        <MeetingDetail
          meeting={selectedMeeting}
          users={users}
          goals={goals}
          onBack={() => setSelectedMeeting(null)}
          onUpdate={loadData}
        />
      ) : (
        <div className="grid gap-4">
          {meetings.length > 0 ? meetings.map(meeting => (
            <Card key={meeting.id} className="border border-slate-200 hover:border-slate-300 transition-colors cursor-pointer" onClick={() => setSelectedMeeting(meeting)}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{meeting.meeting_type}</h3>
                      <p className="text-slate-500">{formatDate(meeting.date)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-600">{meeting.participants?.length || 0} participants</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-600">{meeting.rows?.length || 0} agenda items</span>
                    </div>
                    {canDelete() && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); handleDelete(meeting.id); }}
                        data-testid={`delete-meeting-${meeting.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )) : (
            <Card className="border border-dashed border-slate-300">
              <CardContent className="py-12 text-center">
                <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-semibold mb-2">No meetings scheduled</h3>
                <p className="text-slate-500 mb-4">Create a meeting to get started</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

function CreateMeetingDialog({ templates, users, goals, onClose, onSave }) {
  const [form, setForm] = useState({
    template_id: '',
    date: new Date().toISOString().split('T')[0],
    participants: [],
    notes: '',
    linked_goal_id: null,
    linked_milestone_id: null
  });
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadMilestones = async (goalId) => {
    if (!goalId) {
      setMilestones([]);
      return;
    }
    try {
      const response = await milestonesApi.getAll({ goal_id: goalId });
      setMilestones(response.data);
    } catch (error) {
      console.error('Failed to load milestones:', error);
    }
  };

  const handleGoalChange = (goalId) => {
    // Handle "none" selection
    const newGoalId = goalId === '__none__' ? null : goalId;
    setForm(prev => ({ ...prev, linked_goal_id: newGoalId, linked_milestone_id: null }));
    loadMilestones(newGoalId);
  };

  const handleMilestoneChange = (milestoneId) => {
    // Handle "none" selection
    const newMilestoneId = milestoneId === '__none__' ? null : milestoneId;
    setForm(prev => ({ ...prev, linked_milestone_id: newMilestoneId }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.template_id) {
      toast.error('Please select a meeting template');
      return;
    }
    if (form.participants.length === 0) {
      toast.error('Please select at least one participant');
      return;
    }
    setLoading(true);
    try {
      const submitData = {
        ...form,
        linked_goal_id: form.linked_goal_id || null,
        linked_milestone_id: form.linked_milestone_id || null
      };
      await meetingsApi.create(submitData);
      toast.success('Meeting created');
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create meeting');
    } finally {
      setLoading(false);
    }
  };

  const toggleParticipant = (userId) => {
    setForm(prev => ({
      ...prev,
      participants: prev.participants.includes(userId)
        ? prev.participants.filter(id => id !== userId)
        : [...prev.participants, userId]
    }));
  };

  // Filter goals and milestones - ensure valid IDs only
  const validGoals = goals.filter(g => g && g.id && String(g.id).trim().length > 0 && g.status === 'Active');
  const validMilestones = milestones.filter(m => m && m.id && String(m.id).trim().length > 0);
  const validTemplates = templates.filter(t => t && t.id && String(t.id).trim().length > 0);

  return (
    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Create Meeting</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label>Meeting Template</Label>
          <Select value={form.template_id || undefined} onValueChange={(v) => setForm({...form, template_id: v})}>
            <SelectTrigger data-testid="meeting-template-select">
              <SelectValue placeholder="Select template" />
            </SelectTrigger>
            <SelectContent>
              {validTemplates.map(t => (
                <SelectItem key={t.id} value={t.id}>{t.meeting_type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Date</Label>
          <Input
            type="date"
            value={form.date}
            onChange={(e) => setForm({...form, date: e.target.value})}
            required
            data-testid="meeting-date-input"
          />
        </div>

        <div className="space-y-2">
          <Label>Participants</Label>
          <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
            {users.map(user => (
              <div key={user.id} className="flex items-center gap-2">
                <Checkbox
                  checked={form.participants.includes(user.id)}
                  onCheckedChange={() => toggleParticipant(user.id)}
                  data-testid={`participant-${user.id}`}
                />
                <span className="text-sm">{user.name}</span>
                <Badge variant="outline" className="text-xs ml-auto">{user.role}</Badge>
              </div>
            ))}
          </div>
        </div>

        {/* Goal/Milestone Linking */}
        <div className="space-y-2 p-4 bg-slate-50 rounded-lg">
          <Label className="flex items-center gap-2">
            <Target className="w-4 h-4" />
            Link to Goal (required for Weekly/15-Day meetings)
          </Label>
          <Select value={form.linked_goal_id || '__none__'} onValueChange={handleGoalChange}>
            <SelectTrigger data-testid="meeting-goal-select">
              <SelectValue placeholder="Select a goal to track" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">None</SelectItem>
              {validGoals.map(g => (
                <SelectItem key={g.id} value={g.id}>{g.title} ({g.horizon})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {form.linked_goal_id && validMilestones.length > 0 && (
            <div className="mt-2">
              <Label className="text-sm">Milestone (required for Weekly/15-Day meetings)</Label>
              <Select value={form.linked_milestone_id || '__none__'} onValueChange={handleMilestoneChange}>
                <SelectTrigger data-testid="meeting-milestone-select">
                  <SelectValue placeholder="Select milestone" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {validMilestones.map(m => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.milestone_type}: {formatDate(m.start_date)} - {formatDate(m.end_date)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label>Notes (optional)</Label>
          <Textarea
            value={form.notes}
            onChange={(e) => setForm({...form, notes: e.target.value})}
            placeholder="Meeting notes..."
            data-testid="meeting-notes-input"
          />
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={loading} data-testid="save-meeting-btn">
            {loading ? 'Creating...' : 'Create Meeting'}
          </Button>
        </div>
      </form>
    </DialogContent>
  );
}

function MeetingDetail({ meeting, users, goals, onBack, onUpdate }) {
  const { user, isAdmin } = useAuth();
  const [memberRecords, setMemberRecords] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [meetingData, setMeetingData] = useState(meeting);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMeetingDetail();
  }, [meeting.id]);

  const loadMeetingDetail = async () => {
    try {
      const [meetingRes, recordsRes] = await Promise.all([
        meetingsApi.getOne(meeting.id),
        memberRecordsApi.getAll({ meeting_id: meeting.id })
      ]);
      setMeetingData(meetingRes.data);
      setMemberRecords(recordsRes.data);
    } catch (error) {
      console.error('Failed to load meeting details:', error);
    } finally {
      setLoading(false);
    }
  };

  const getUserName = (userId) => users.find(u => u.id === userId)?.name || 'Unknown';
  const getGoalTitle = (goalId) => goals.find(g => g.id === goalId)?.title || '';

  const updateRow = async (rowIndex, field, value) => {
    // Create updated rows from current meetingData state
    const newRows = meetingData.rows.map((row, idx) => 
      idx === rowIndex ? { ...row, [field]: value } : { ...row }
    );
    
    // Optimistically update local state first
    setMeetingData(prev => ({ ...prev, rows: newRows }));
    
    try {
      await meetingsApi.update(meetingData.id, { rows: newRows });
      toast.success('Updated');
    } catch (error) {
      // Revert on failure
      toast.error('Failed to update');
      loadMeetingDetail(); // Refetch to get correct state
    }
  };

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={onBack} className="mb-4">
        ← Back to Meetings
      </Button>

      {/* Meeting Header */}
      <Card className="border border-slate-200">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">{meetingData.meeting_type}</h2>
              <p className="text-slate-500">{formatDate(meetingData.date)}</p>
              {meetingData.linked_goal_id && (
                <div className="flex items-center gap-2 mt-2">
                  <Target className="w-4 h-4 text-blue-500" />
                  <span className="text-sm text-blue-600">
                    Linked to: {getGoalTitle(meetingData.linked_goal_id)}
                  </span>
                </div>
              )}
            </div>
            <Badge variant="outline" className="text-lg px-4 py-2">
              {meetingData.participants?.length || 0} Participants
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Milestone Summary */}
      {meetingData.milestone_summary && (
        <Card className={`border ${meetingData.milestone_summary.deficit?.is_behind ? 'border-red-200 bg-red-50/50' : 'border-emerald-200 bg-emerald-50/50'}`}>
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${meetingData.milestone_summary.deficit?.is_behind ? 'bg-red-100' : 'bg-emerald-100'}`}>
                {meetingData.milestone_summary.deficit?.is_behind ? (
                  <TrendingDown className="w-5 h-5 text-red-600" />
                ) : (
                  <Target className="w-5 h-5 text-emerald-600" />
                )}
              </div>
              <div className="flex-1">
                <h4 className="font-semibold">{meetingData.milestone_summary.goal_title}</h4>
                {meetingData.milestone_summary.milestone && (
                  <div className="mt-2 grid grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Milestone:</span>
                      <p className="font-medium">{meetingData.milestone_summary.milestone.type}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Target:</span>
                      <p className="font-medium">{formatCurrency(meetingData.milestone_summary.milestone.target, meetingData.milestone_summary.unit)}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Actual:</span>
                      <p className="font-medium">{formatCurrency(meetingData.milestone_summary.milestone.actual, meetingData.milestone_summary.unit)}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Progress:</span>
                      <div className="flex items-center gap-2">
                        <Progress value={meetingData.milestone_summary.milestone.progress} className="w-16 h-2" />
                        <span>{Math.round(meetingData.milestone_summary.milestone.progress)}%</span>
                      </div>
                    </div>
                  </div>
                )}
                {meetingData.milestone_summary.deficit?.is_behind && (
                  <Alert className="mt-3 bg-red-100 border-red-300">
                    <TrendingDown className="h-4 w-4 text-red-600" />
                    <AlertTitle className="text-red-700">Behind Target</AlertTitle>
                    <AlertDescription className="text-red-600">
                      Deficit: {formatCurrency(meetingData.milestone_summary.deficit.deficit, meetingData.milestone_summary.unit)}
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="agenda" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="agenda">Agenda & Notes</TabsTrigger>
          <TabsTrigger value="members">Member Records</TabsTrigger>
        </TabsList>

        <TabsContent value="agenda">
          {/* Agenda Rows */}
          <div className="space-y-4">
            {meetingData.rows?.map((row, idx) => (
              <Card key={idx} className="border border-slate-200">
                <CardContent className="p-4">
                  <div className="grid grid-cols-5 gap-4">
                    <div>
                      <Label className="text-xs text-slate-500">Duration</Label>
                      <p className="font-medium">{row.time_duration}</p>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs text-slate-500">Agenda</Label>
                      <p className="font-medium">{row.agenda}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Facilitator</Label>
                      <Input
                        value={row.facilitator || ''}
                        onChange={(e) => updateRow(idx, 'facilitator', e.target.value)}
                        placeholder="Name..."
                        className="h-8 mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Highlights</Label>
                      <Input
                        value={row.highlights || ''}
                        onChange={(e) => updateRow(idx, 'highlights', e.target.value)}
                        placeholder="Key points..."
                        className="h-8 mt-1"
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label className="text-xs text-slate-500">Actionables</Label>
                    <Textarea
                      value={row.actionables || ''}
                      onChange={(e) => updateRow(idx, 'actionables', e.target.value)}
                      placeholder="Action items..."
                      className="mt-1 h-20"
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="members">
          {/* Member Records */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Members List */}
            <Card className="border border-slate-200">
              <CardHeader>
                <CardTitle className="text-base">Participants</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {meetingData.participants?.map(participantId => {
                  const record = memberRecords.find(r => r.user_id === participantId);
                  return (
                    <div
                      key={participantId}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${selectedMemberId === participantId ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50 hover:bg-slate-100'}`}
                      onClick={() => setSelectedMemberId(participantId)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                          <span className="text-sm font-semibold">{getUserName(participantId).charAt(0)}</span>
                        </div>
                        <span className="font-medium">{getUserName(participantId)}</span>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Member Record Detail */}
            <div className="lg:col-span-2">
              {selectedMemberId ? (
                <MemberRecordForm
                  record={memberRecords.find(r => r.user_id === selectedMemberId)}
                  users={users}
                  onUpdate={loadMeetingDetail}
                />
              ) : (
                <Card className="border border-dashed border-slate-300">
                  <CardContent className="py-12 text-center text-slate-500">
                    <Users className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                    <p>Select a participant to view/edit their record</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MemberRecordForm({ record, users, onUpdate }) {
  const [form, setForm] = useState({
    outcome_review: record?.outcome_review || {},
    feedback_qa: record?.feedback_qa || [],
    oea_art: record?.oea_art || [],
    problems_solutions: record?.problems_solutions || [],
    commitments: record?.commitments || []
  });
  const [saving, setSaving] = useState(false);

  if (!record) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await memberRecordsApi.update(record.id, form);
      toast.success('Record saved');
      onUpdate();
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addItem = (field, template) => {
    setForm(prev => ({
      ...prev,
      [field]: [...prev[field], template]
    }));
  };

  const updateItem = (field, index, key, value) => {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? { ...item, [key]: value } : item)
    }));
  };

  const removeItem = (field, index) => {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  return (
    <Card className="border border-slate-200">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Member Record</CardTitle>
        <Button onClick={handleSave} disabled={saving} data-testid="save-member-record-btn">
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </CardHeader>
      <CardContent>
        <Accordion type="multiple" defaultValue={['outcome', 'feedback', 'oea', 'problems', 'commitments']}>
          {/* Outcome Review */}
          <AccordionItem value="outcome">
            <AccordionTrigger>A) Outcome Review</AccordionTrigger>
            <AccordionContent className="space-y-3">
              <div>
                <Label className="text-xs">Outcomes Committed</Label>
                <Textarea
                  value={form.outcome_review.outcomes_committed || ''}
                  onChange={(e) => setForm({...form, outcome_review: {...form.outcome_review, outcomes_committed: e.target.value}})}
                  placeholder="List committed outcomes..."
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Outcomes Achieved</Label>
                <Textarea
                  value={form.outcome_review.outcomes_achieved || ''}
                  onChange={(e) => setForm({...form, outcome_review: {...form.outcome_review, outcomes_achieved: e.target.value}})}
                  placeholder="List achieved outcomes..."
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Key Wins</Label>
                  <Textarea
                    value={form.outcome_review.key_wins || ''}
                    onChange={(e) => setForm({...form, outcome_review: {...form.outcome_review, key_wins: e.target.value}})}
                    className="mt-1 h-20"
                  />
                </div>
                <div>
                  <Label className="text-xs">Breakdowns/Gaps</Label>
                  <Textarea
                    value={form.outcome_review.breakdowns || ''}
                    onChange={(e) => setForm({...form, outcome_review: {...form.outcome_review, breakdowns: e.target.value}})}
                    className="mt-1 h-20"
                  />
                </div>
                <div>
                  <Label className="text-xs">Blockers</Label>
                  <Textarea
                    value={form.outcome_review.blockers || ''}
                    onChange={(e) => setForm({...form, outcome_review: {...form.outcome_review, blockers: e.target.value}})}
                    className="mt-1 h-20"
                  />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Feedback Q&A */}
          <AccordionItem value="feedback">
            <AccordionTrigger>B) Feedback Q&A</AccordionTrigger>
            <AccordionContent>
              {form.feedback_qa.map((fb, idx) => (
                <div key={idx} className="p-3 bg-slate-50 rounded-lg mb-2 space-y-2">
                  <div className="flex justify-between">
                    <Label className="text-xs">Feedback #{idx + 1}</Label>
                    <Button variant="ghost" size="sm" onClick={() => removeItem('feedback_qa', idx)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  <Input
                    placeholder="Question"
                    value={fb.question}
                    onChange={(e) => updateItem('feedback_qa', idx, 'question', e.target.value)}
                  />
                  <Textarea
                    placeholder="Answer"
                    value={fb.answer}
                    onChange={(e) => updateItem('feedback_qa', idx, 'answer', e.target.value)}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Select value={fb.rating ? fb.rating.toString() : undefined} onValueChange={(v) => updateItem('feedback_qa', idx, 'rating', parseInt(v))}>
                      <SelectTrigger><SelectValue placeholder="Rating (1-5)" /></SelectTrigger>
                      <SelectContent>
                        {[1,2,3,4,5].map(r => <SelectItem key={r} value={r.toString()}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => addItem('feedback_qa', { question: '', answer: '', rating: null, given_by: '', received_by: '' })}>
                <Plus className="w-3 h-3 mr-1" /> Add Feedback
              </Button>
            </AccordionContent>
          </AccordionItem>

          {/* OEA-ART */}
          <AccordionItem value="oea">
            <AccordionTrigger>C) OEA-ART Goal Creation</AccordionTrigger>
            <AccordionContent>
              <div className="mb-3 p-2 bg-blue-50 rounded text-xs text-blue-700">
                <strong>OEA:</strong> O = Outcome, E = Emotions/Why, A = Action Steps<br/>
                <strong>ART:</strong> Aspirational, Realistic, Transformational
              </div>
              {form.oea_art.map((oea, idx) => (
                <div key={idx} className="p-3 bg-slate-50 rounded-lg mb-2 space-y-2">
                  <div className="flex justify-between">
                    <Label className="text-xs">Goal #{idx + 1}</Label>
                    <Button variant="ghost" size="sm" onClick={() => removeItem('oea_art', idx)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  <Input placeholder="Outcome (O) - What result do you want?" value={oea.objective} onChange={(e) => updateItem('oea_art', idx, 'objective', e.target.value)} />
                  <Textarea placeholder="Emotions / Why (E) - Why does this matter?" value={oea.evidence} onChange={(e) => updateItem('oea_art', idx, 'evidence', e.target.value)} className="h-16" />
                  <Textarea placeholder="Action Steps (A) - What steps will you take?" value={oea.actions} onChange={(e) => updateItem('oea_art', idx, 'actions', e.target.value)} className="h-16" />
                  <Input placeholder="Resources / Support Needed" value={oea.resources} onChange={(e) => updateItem('oea_art', idx, 'resources', e.target.value)} />
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="date" value={oea.timeline} onChange={(e) => updateItem('oea_art', idx, 'timeline', e.target.value)} />
                    <Select value={oea.status || undefined} onValueChange={(v) => updateItem('oea_art', idx, 'status', v)}>
                      <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Active">Active</SelectItem>
                        <SelectItem value="Done">Done</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => addItem('oea_art', { objective: '', evidence: '', actions: '', resources: '', timeline: '', owner: '', status: 'Active' })}>
                <Plus className="w-3 h-3 mr-1" /> Add OEA-ART Goal
              </Button>
            </AccordionContent>
          </AccordionItem>

          {/* Problems & Solutions */}
          <AccordionItem value="problems">
            <AccordionTrigger>D) Problems & Solutions</AccordionTrigger>
            <AccordionContent>
              {form.problems_solutions.map((ps, idx) => (
                <div key={idx} className="p-3 bg-slate-50 rounded-lg mb-2 space-y-2">
                  <div className="flex justify-between">
                    <Label className="text-xs">Problem #{idx + 1}</Label>
                    <Button variant="ghost" size="sm" onClick={() => removeItem('problems_solutions', idx)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  <Textarea placeholder="Problem" value={ps.problem} onChange={(e) => updateItem('problems_solutions', idx, 'problem', e.target.value)} className="h-16" />
                  <Input placeholder="Root Cause (optional)" value={ps.root_cause} onChange={(e) => updateItem('problems_solutions', idx, 'root_cause', e.target.value)} />
                  <Textarea placeholder="Solution/Decision" value={ps.solution} onChange={(e) => updateItem('problems_solutions', idx, 'solution', e.target.value)} className="h-16" />
                  <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="Action Owner" value={ps.action_owner} onChange={(e) => updateItem('problems_solutions', idx, 'action_owner', e.target.value)} />
                    <Input type="date" value={ps.due_date} onChange={(e) => updateItem('problems_solutions', idx, 'due_date', e.target.value)} />
                    <Select value={ps.status || undefined} onValueChange={(v) => updateItem('problems_solutions', idx, 'status', v)}>
                      <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Open">Open</SelectItem>
                        <SelectItem value="Done">Done</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => addItem('problems_solutions', { problem: '', root_cause: '', solution: '', action_owner: '', due_date: '', status: 'Open' })}>
                <Plus className="w-3 h-3 mr-1" /> Add Problem
              </Button>
            </AccordionContent>
          </AccordionItem>

          {/* Commitments */}
          <AccordionItem value="commitments">
            <AccordionTrigger>E) Meeting Commitments</AccordionTrigger>
            <AccordionContent>
              {form.commitments.map((c, idx) => (
                <div key={idx} className="p-3 bg-slate-50 rounded-lg mb-2 space-y-2">
                  <div className="flex justify-between">
                    <Label className="text-xs">Commitment #{idx + 1}</Label>
                    <Button variant="ghost" size="sm" onClick={() => removeItem('commitments', idx)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  <Textarea placeholder="Commitment" value={c.commitment} onChange={(e) => updateItem('commitments', idx, 'commitment', e.target.value)} />
                  <Input type="date" placeholder="Follow-up date" value={c.follow_up_date} onChange={(e) => updateItem('commitments', idx, 'follow_up_date', e.target.value)} />
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => addItem('commitments', { commitment: '', follow_up_date: '' })}>
                <Plus className="w-3 h-3 mr-1" /> Add Commitment
              </Button>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  );
}
